import { pipe, map, mergeMap, tap } from 'wonka';
import * as Sentry from '@sentry/react';
import { getSpanStatusFromHttpCode } from '@sentry/core';
import { SENTRY_GRAPHQL_FLOW_NAMES } from './sentry-graphql.constanst';
import { sanitizeGraphQLQuery, sanitizeGraphQLVariables } from './sanitize-filtered';

export const sentryExchange = ({ forward }: any) => (ops$: any) => {
  return pipe(
    ops$,
    map((operation: any) => {
      const { query, variables } = operation;
      let traceHeader, baggageHeader, sentrySpan;

      if (query.definitions[0] && query.definitions[0].name) {
        const operationName = query.definitions[0].name.value;

        if (SENTRY_GRAPHQL_FLOW_NAMES.hasOwnProperty(operationName)) {
          const OP = SENTRY_GRAPHQL_FLOW_NAMES[operationName].op;
          const FLOW_NAME = SENTRY_GRAPHQL_FLOW_NAMES[operationName].flowName;
          const _query = query.loc?.source?.body;
          const payload = variables;
          
          // Crear span pero NO finalizarlo aún
          sentrySpan = Sentry.startInactiveSpan({
            name: `${FLOW_NAME}.networking`,
            op: OP,
          });

          traceHeader = Sentry.spanToTraceHeader(sentrySpan);
          baggageHeader = Sentry.spanToBaggageHeader(sentrySpan);

          sentrySpan.setAttributes({
            'flow.type': 'graphql',
            'flow.name': FLOW_NAME,
            'flow.status': 'started',
            'graphql.operation.name': operationName,
            'graphql.operation.query': sanitizeGraphQLQuery(_query),
            'graphql.operation.variables': sanitizeGraphQLVariables(payload),
          });

          if(operation.context?.sentryTask){
            const sentryTask = operation.context.sentryTask;
            sentrySpan.setAttributes({
              'task.id': sentryTask._id,
              'task.key': sentryTask.key,
              'task.name': sentryTask.name,
            });
          }

          if(operation.context?.sentryLoan){
            const sentryLoan = operation.context.sentryLoan;
            sentrySpan.setAttributes({
              'loan.id': sentryLoan._id,
              'loan.name': sentryLoan.name,
              'loan.type': sentryLoan.type,
              'loan.loanNumberJungo': sentryLoan.loanNumberJungo,
            });
          }

          operation.context.sentrySpan = sentrySpan;
        }
      }

      const headers: any = {};

      if (traceHeader) headers['sentry-trace'] = traceHeader;
      if (baggageHeader) headers['baggage'] = baggageHeader;

      return {
        ...operation,
        context: {
          ...operation.context,
          fetchOptions: {
            ...operation.context.fetchOptions,
            headers: {
              ...(operation.context.fetchOptions?.headers || {}),
              ...headers
            }
          }
        }
      };
    }),
    forward,
    tap((result: any) => {
      const { operation, data, error } = result;
      const sentrySpan = operation.context.sentrySpan;

      if (!sentrySpan) return;

      try {
        const operationName = operation.query.definitions[0]?.name?.value;

        if (SENTRY_GRAPHQL_FLOW_NAMES.hasOwnProperty(operationName)) {
          const traceHeader = Sentry.spanToTraceHeader(sentrySpan);
          const baggageHeader = Sentry.spanToBaggageHeader(sentrySpan);

          Sentry.continueTrace(
            { sentryTrace: traceHeader, baggage: baggageHeader },
            () => {
              updateSpanWithResponse(sentrySpan, data, error, operation);
            }
          );
        } else {
          updateSpanWithResponse(sentrySpan, data, error, operation);
        }
      }
      catch (spanError) {
        console.warn('Error updating Sentry span:', spanError);
        sentrySpan.end();
      }
    })
  );
};

function updateSpanWithResponse(span: any, data: any, error: any, operation: any) {
  const hasErrors = error || (data && data.errors);
  const status = hasErrors ? 'error' : 'success';

  span.setAttributes({
    'flow.status': status,
    'graphql.response.status': status,
    'graphql.response.has_errors': hasErrors,
    'graphql.response.errors_count': hasErrors ?
      (error ? 1 : (data.errors?.length || 0)) : 0,
    'graphql.response.timestamp': new Date().toISOString(),
  });

  if (hasErrors) {
    //TODO Falta hacer dinamico el tipo de error
    span.setStatus(getSpanStatusFromHttpCode(500));

    // Capturar el payload completo en caso de error
    //captureErrorPayload(operation, data, error);
  } else {
    span.setStatus(getSpanStatusFromHttpCode(200));
  }

  span.end();
}

function captureErrorPayload(operation: any, data: any, error: any) {
  Sentry.withScope((scope) => {
    const operationName = operation.query.definitions[0]?.name?.value;
    const query = operation.query.loc?.source?.body;
    const variables = operation.variables;

    // Configurar contexto de la operación
    scope.setContext('GraphQL Operation', {
      'Operation Name': operationName,
      'Query': query,
      'Variables': JSON.stringify(variables),
    });

    // Configurar contexto de la respuesta
    scope.setContext('GraphQL Response', {
      'Response Data': data ? JSON.stringify(data, null, 2) : 'No data',
      'Response Errors': error ?
        JSON.stringify(error, Object.getOwnPropertyNames(error), 2) :
        (data?.errors ? JSON.stringify(data.errors, null, 2) : 'No errors'),
      'Has Data': !!data,
      'Has Errors': !!error || !!(data && data.errors),
    });

    // Agregar tags para mejor filtrado
    scope.setTag('graphql.operation.name', operationName || 'unknown');
    scope.setTag('graphql.operation.type', 'error');
    scope.setTag('flow.type', 'graphql');

    // Capturar la excepción
    const exception = error || new Error(`GraphQL operation ${operationName} failed`);
    exception.name = 'GraphQLError';

    Sentry.captureException(exception, {
      contexts: {
        operation: {
          name: operationName,
          query: query,
          variables: variables,
        },
        response: {
          data: data,
          errors: error ? [error] : data?.errors,
        }
      }
    });
  });
}